import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DepartmentMasterService } from '../../../services/department-master.service';
import { Department } from '../../../models/user.model';
import { DataTableComponent, TableColumn } from '../../../components/shared/data-table/data-table.component';
import { AdminModalComponent } from '../../../components/shared/admin-modal/admin-modal.component';

@Component({
  selector: 'app-department-management',
  standalone: true,
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    DataTableComponent, 
    AdminModalComponent
  ],
  templateUrl: './department-management.component.html',
  styleUrls: ['./department-management.component.scss']
})
export class DepartmentManagementComponent implements OnInit {
  private deptService = inject(DepartmentMasterService);
  private fb = inject(FormBuilder);

  departments = signal<Department[]>([]);
  loading = signal(false);
  isModalOpen = signal(false);
  isSubmitting = signal(false);
  
  deptForm: FormGroup;
  editingDept: Department | null = null;

  columns: TableColumn[] = [
    { key: 'name', label: 'Department Name', sortable: true },
    { key: 'isActive', label: 'Status', type: 'badge' },
    { key: 'actions', label: 'Actions', type: 'actions' }
  ];

  totalActive = computed(() => this.departments().filter(d => d.isActive).length);
  totalInactive = computed(() => this.departments().filter(d => !d.isActive).length);

  constructor() {
    this.deptForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      isActive: [true]
    });
  }

  ngOnInit(): void {
    this.loadData();
  }

  async loadData() {
    this.loading.set(true);
    try {
      const depts = await this.deptService.getDepartments();
      this.departments.set(depts);
    } catch (error) {
      console.error('Error loading departments:', error);
    } finally {
      this.loading.set(false);
    }
  }

  openCreateModal() {
    this.editingDept = null;
    this.deptForm.reset({ isActive: true });
    this.isModalOpen.set(true);
  }

  openEditModal(dept: Department) {
    this.editingDept = dept;
    this.deptForm.patchValue(dept);
    this.isModalOpen.set(true);
  }

  async handleTableAction(event: { type: string, item: any }) {
    if (event.type === 'edit') {
      this.openEditModal(event.item);
    } else if (event.type === 'delete') {
      if (confirm(`Are you sure you want to ${event.item.isActive ? 'deactivate' : 'activate'} this department?`)) {
        await this.deptService.toggleStatus(event.item.id, !event.item.isActive);
        this.loadData();
      }
    }
  }

  async submitForm() {
    if (this.deptForm.invalid) return;

    this.isSubmitting.set(true);
    try {
      const { name } = this.deptForm.value;
      if (this.editingDept) {
        await this.deptService.updateDepartment(this.editingDept.id, name);
      } else {
        await this.deptService.addDepartment(name);
      }
      this.isModalOpen.set(false);
      this.loadData();
    } catch (error) {
      console.error('Error saving department:', error);
    } finally {
      this.isSubmitting.set(false);
    }
  }
}
